import type { ResultsType, CategoriaGasto, ItemGasto } from "../calculadora-custos-fixos_d";

/**
 * Sanitiza valor BRL para número
 * @returns {number} Valor numérico sanitizado
 */
export function sanitizeBrlValue(value: string): number {
  if (!value || value.trim() === '') return 0;
  
  let cleaned = value.trim();
  
  if (cleaned.includes(',')) {
    cleaned = cleaned.replace(/\./g, '');
  }
  
  cleaned = cleaned.replace(',', '.');
  
  cleaned = cleaned.replace(/[^\d.-]/g, '');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Formata valor para padrão BRL durante digitação
 */
export function formatBrlInput(value: string): string {
  let numbers = value.replace(/\D/g, '');
  
  if (!numbers) return '';
  
  numbers = numbers.replace(/^0+/, '') || '0';
  
  if (numbers.length === 1) numbers = '00' + numbers;
  if (numbers.length === 2) numbers = '0' + numbers;
  
  const cents = numbers.slice(-2);
  const reais = numbers.slice(0, -2);
  
  const reaisFormatted = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${reaisFormatted},${cents}`;
}

/**
 * Calcula o total mensal de uma lista de gastos considerando frequência
 */
function calcularTotalMensalGastos(
  gastos: Array<{ valor: number; frequencia: number }>
): number {
  return gastos.reduce((total, gasto) => {
    if (!gasto.valor || gasto.valor <= 0) return total;
    const valorMensal = gasto.valor / gasto.frequencia;
    return total + valorMensal;
  }, 0);
}

/**
 * Cria array de itens de gasto detalhados
 */
function criarItensGasto(
  gastos: Array<{ descricao: string; valor: number; frequencia: number }>
): Array<ItemGasto> {
  return gastos
    .filter(g => g.valor > 0 && g.descricao)
    .map(g => ({
      descricao: g.descricao,
      valor: g.valor,
      valorMensal: Number((g.valor / g.frequencia).toFixed(2))
    }));
}

/**
 * Função principal de cálculo
 */
export function calcularCustosFixos(dados: {
  rendaLiquida: number;
  moradia: Array<{ descricao: string; valor: number; frequencia: number }>;
  transporte: Array<{ descricao: string; valor: number; frequencia: number }>;
  saude: Array<{ descricao: string; valor: number; frequencia: number }>;
  alimentacao: Array<{ descricao: string; valor: number; frequencia: number }>;
  estiloVida: Array<{ descricao: string; valor: number; frequencia: number }>;
}): ResultsType {
  
  const { rendaLiquida, moradia, transporte, saude, alimentacao, estiloVida } = dados;
  
  // 1. Calcular totais mensais por categoria
  const totalMoradia = calcularTotalMensalGastos(moradia);
  const totalTransporte = calcularTotalMensalGastos(transporte);
  const totalSaude = calcularTotalMensalGastos(saude);
  const totalAlimentacao = calcularTotalMensalGastos(alimentacao);
  const totalEstiloVida = calcularTotalMensalGastos(estiloVida);
  
  // 2. Totais por tipo (essenciais vs estilo de vida)
  const custosEssenciais = totalMoradia + totalTransporte + totalSaude + totalAlimentacao;
  const custosEstiloVida = totalEstiloVida;
  const custosTotal = custosEssenciais + custosEstiloVida;
  
  // 3. Calcular percentuais
  const percentualComprometido = rendaLiquida > 0 
    ? Number(((custosTotal / rendaLiquida) * 100).toFixed(2))
    : 0;
  
  const percentualEssencial = rendaLiquida > 0
    ? Number(((custosEssenciais / rendaLiquida) * 100).toFixed(2))
    : 0;
  
  const percentualEstiloVida = rendaLiquida > 0
    ? Number(((custosEstiloVida / rendaLiquida) * 100).toFixed(2))
    : 0;
  
  // 4. Valores disponíveis
  const valorDisponivel = Number((rendaLiquida - custosTotal).toFixed(2));
  const percentualDisponivel = rendaLiquida > 0
    ? Number(((valorDisponivel / rendaLiquida) * 100).toFixed(2))
    : 0;
  
  // 5. Meta de investimento (25% da renda)
  const valorIdealInvestimento = Number((rendaLiquida * 0.25).toFixed(2));
  const gap = Number((valorDisponivel - valorIdealInvestimento).toFixed(2));
  
  // 6. Definir cenário
  let cenario: 'critico' | 'atencao' | 'equilibrado' | 'excelente';
  if (percentualComprometido > 80) {
    cenario = 'critico';
  } else if (percentualComprometido > 70) {
    cenario = 'atencao';
  } else if (percentualComprometido >= 50) {
    cenario = 'equilibrado';
  } else {
    cenario = 'excelente';
  }
  
  // 7. Criar array de categorias detalhadas
  const categorias: Array<CategoriaGasto> = [];
  
  if (totalMoradia > 0) {
    categorias.push({
      nome: 'Moradia',
      total: Number(totalMoradia.toFixed(2)),
      percentual: custosTotal > 0 
        ? Number(((totalMoradia / custosTotal) * 100).toFixed(1))
        : 0,
      itens: criarItensGasto(moradia),
      essencial: true
    });
  }
  
  if (totalTransporte > 0) {
    categorias.push({
      nome: 'Transporte',
      total: Number(totalTransporte.toFixed(2)),
      percentual: custosTotal > 0
        ? Number(((totalTransporte / custosTotal) * 100).toFixed(1))
        : 0,
      itens: criarItensGasto(transporte),
      essencial: true
    });
  }
  
  if (totalSaude > 0) {
    categorias.push({
      nome: 'Saúde & Bem-estar',
      total: Number(totalSaude.toFixed(2)),
      percentual: custosTotal > 0
        ? Number(((totalSaude / custosTotal) * 100).toFixed(1))
        : 0,
      itens: criarItensGasto(saude),
      essencial: true
    });
  }
  
  if (totalAlimentacao > 0) {
    categorias.push({
      nome: 'Alimentação',
      total: Number(totalAlimentacao.toFixed(2)),
      percentual: custosTotal > 0
        ? Number(((totalAlimentacao / custosTotal) * 100).toFixed(1))
        : 0,
      itens: criarItensGasto(alimentacao),
      essencial: true
    });
  }
  
  if (totalEstiloVida > 0) {
    categorias.push({
      nome: 'Estilo de Vida',
      total: Number(totalEstiloVida.toFixed(2)),
      percentual: custosTotal > 0
        ? Number(((totalEstiloVida / custosTotal) * 100).toFixed(1))
        : 0,
      itens: criarItensGasto(estiloVida),
      essencial: false
    });
  }
  
  return {
    rendaLiquida: Number(rendaLiquida.toFixed(2)),
    custosTotal: Number(custosTotal.toFixed(2)),
    custosEssenciais: Number(custosEssenciais.toFixed(2)),
    custosEstiloVida: Number(custosEstiloVida.toFixed(2)),
    percentualComprometido,
    percentualEssencial,
    percentualEstiloVida,
    percentualDisponivel,
    valorDisponivel,
    valorIdealInvestimento,
    gap,
    categorias,
    cenario
  };
}